import PropTypes from 'prop-types'
import { useState, useEffect, useRef, useCallback } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { obtenerReferencia, obtenerURL } from '../../helpers/notificaciones/rutasNotificacion.js'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import { setReiniciaFiltros } from '../../slices/filterSlice'
import * as notificacionesService from '../servicios/notificacionesService'
import LinkButton from '../ui/LinkButton'
import styles from './DropdownNotificacion.module.css'

const CANT_MAX_NOTIFICACIONES = 99
const TAMANO_PAGINA = 20
const INTERVALO_REFRESCO_MS = 20000

const DropdownNotificacion = ({ refreshKey }) => {
  const [notificaciones, setNotificaciones] = useState([])
  const [notificacionExistente, setNotificacionExistente] = useState(false)
  const [notificacionLeida, setNotificacionLeida] = useState(0)
  const [totalRegistros, setTotalRegistros] = useState(0)
  const [show, setShow] = useState(false)
  const dispatch = useDispatch()
  const { loginDataContext: { idUsuario: idUsuarioChat } = {} } = useAuth()
  const location = useLocation()

  const navigate = useNavigate()

  const hayNoLeidas = notificacionLeida > 0

  const handleToggle = (nextShow) => {
    setShow(location.pathname === '/Notificaciones' ? false : nextShow)
  }

  const cerrarDropdown = () => setShow(false)

  const fetchNotificaciones = useCallback(async () => {
    if (!idUsuarioChat || location.pathname === '/Notificaciones') return
    try {
      const { data } = await notificacionesService.buscarPorMensaje(idUsuarioChat, 1, TAMANO_PAGINA)
      const listado = Array.isArray(data?.notificaciones) ? data.notificaciones : []
      setNotificaciones(listado)
      setTotalRegistros(data?.totalRegistros ?? 0)
      setNotificacionLeida(data?.totalNoLeidas ?? 0)
      setNotificacionExistente(listado.length !== 0)
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al obtener las notificaciones.', 'notificacion')
    }
  }, [idUsuarioChat, location.pathname])

  const fetchRef = useRef(fetchNotificaciones)
  fetchRef.current = fetchNotificaciones

  useEffect(() => {
    if (!idUsuarioChat || location.pathname === '/Notificaciones') return

    fetchRef.current()

    let intervalo = null
    const iniciarPolling = () => {
      if (intervalo === null) {
        intervalo = setInterval(() => fetchRef.current(), INTERVALO_REFRESCO_MS)
      }
    }
    const detenerPolling = () => {
      clearInterval(intervalo)
      intervalo = null
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        detenerPolling()
      } else {
        fetchRef.current()
        iniciarPolling()
      }
    }

    if (!document.hidden) iniciarPolling()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      detenerPolling()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [idUsuarioChat, location.pathname, refreshKey])

  const handleClickNotificacion = async (idMensaje) => {
    cerrarDropdown()
    if (notificaciones.find((n) => n.idMensaje === idMensaje)?.fechaLeido !== null) return
    try {
      await notificacionesService.actualizarNotificacion({ idMensaje })
      contador(idMensaje)
      dispatch(setReiniciaFiltros(true))
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al actualizar la notificación.', 'notificacion')
    }
  }

  const contador = (idMensaje) => {
    const estabaNoLeida = notificaciones.find((n) => n.idMensaje === idMensaje)?.fechaLeido === null
    const notificacionActualizada = notificaciones.map((n) => {
      return n.idMensaje === idMensaje
        ? { ...n, fechaLeido: n.fechaLeido === null ? 'leido' : null }
        : n
    })

    setNotificaciones(notificacionActualizada)
    setNotificacionLeida((prev) => Math.max(0, prev + (estabaNoLeida ? -1 : 1)))
  }

  const handleToggleLeido = async (e, idMensaje) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await notificacionesService.actualizarNotificacion({ idMensaje })
      contador(idMensaje)
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al actualizar la notificación.', 'notificacion')
    }
  }

  const handleMarcarTodas = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    const leido = hayNoLeidas
    try {
      await notificacionesService.marcarTodas(idUsuarioChat, leido)
      const notificacionesActualizadas = notificaciones.map((n) => ({
        ...n,
        fechaLeido: leido ? 'leido' : null
      }))
      setNotificaciones(notificacionesActualizadas)
      setNotificacionLeida(leido ? 0 : totalRegistros)
      dispatch(setReiniciaFiltros(true))
    } catch (error) {
      toastErrorLog(error, 'Ocurrió un error al actualizar las notificaciones.', 'notificacion')
    }
  }

  const handleConfiguracion = (e) => {
    e.preventDefault()
    e.stopPropagation()
    cerrarDropdown()
    navigate('/Notificaciones')
  }

  return (
    <>
      <Dropdown id="drop-mobile-style" show={show} onToggle={handleToggle}>
        <Dropdown.Toggle id="drop-toggle-style">
          <div className="btn btn-notificaciones">
            <span
              className={`fa fa-bell ${notificacionLeida > 0 ? styles.color_bell_notification : ''}`}
            ></span>
            <span className="badge badge-light">
              {notificacionLeida > CANT_MAX_NOTIFICACIONES ? '99+' : notificacionLeida}
            </span>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className={
            notificacionExistente
              ? styles.dropdown_link_notificacion
              : styles.contenedor_notificaciones_vacias
          }
        >
          <div className={styles.lista_scroll}>
            {notificacionExistente ? (
              notificaciones.map((notificacion) => {
                const referencia = obtenerReferencia(notificacion)
                const url = obtenerURL(notificacion, idUsuarioChat)
                return (
                  <Dropdown.Item
                    as={LinkButton}
                    to={url}
                    key={notificacion.idMensaje}
                    onClick={() => {
                      handleClickNotificacion(notificacion.idMensaje)
                      if (!url) {
                        toastErrorLog(
                          null,
                          'Hubo un error al cargar la información.',
                          'notificacion'
                        )
                        navigate('/Notificaciones')
                      }
                    }}
                    className={`${styles.estilo} ${styles.item_notificacion} ${notificacion.fechaLeido === null ? styles.item_no_leido : ''}`}
                  >
                    <span
                      role="button"
                      title={
                        notificacion.fechaLeido === null
                          ? 'Marcar como leída'
                          : 'Marcar como no leída'
                      }
                      onClick={(e) => handleToggleLeido(e, notificacion.idMensaje)}
                      className={`fa fa-circle ${notificacion.fechaLeido === null ? styles.circulo_dot : styles.circulo_dot_leido}`}
                    />
                    <div
                      className={`${styles.notificacion_nueva} ${notificacion.fechaLeido === null ? styles.texto_no_leido : ''}`}
                    >
                      <span className={styles.encabezado_notificacion}>
                        {notificacion.mensajeSistema}
                        {referencia && (
                          <>
                            {` ${referencia.etiqueta} `}
                            <b>#{referencia.id}</b>
                          </>
                        )}
                      </span>
                      {notificacion.titulo && (
                        <b className={styles.titulo_notificacion_publicacion}>
                          {notificacion.titulo}
                        </b>
                      )}
                      <div className={styles.texto_notificacion}>{notificacion.texto}</div>
                    </div>
                  </Dropdown.Item>
                )
              })
            ) : (
              <div className="notificaciones-vacias">Aún no tienes notificaciones.</div>
            )}
          </div>
          <div className={styles.acciones_notificaciones}>
            <button
              type="button"
              className={styles.boton_accion}
              onClick={handleMarcarTodas}
              disabled={notificaciones.length === 0}
              title={hayNoLeidas ? 'Marcar todas como leídas' : 'Marcar todas como no leídas'}
            >
              <span
                className={`fa ${hayNoLeidas ? 'fa-check-double' : 'fa-envelope'}`}
                aria-hidden="true"
              />
              {hayNoLeidas ? 'Marcar todas como leídas' : 'Marcar todas como no leídas'}
            </button>
            <button
              type="button"
              className={styles.boton_accion}
              onClick={handleConfiguracion}
              title="Ver todas las notificaciones"
            >
              <span aria-hidden="true">Ver todas las notificaciones</span>
            </button>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </>
  )
}

DropdownNotificacion.propTypes = {
  refreshKey: PropTypes.number
}

export default DropdownNotificacion
